import { createUpdateId } from '../../utils/updateId';
const transformSurfaceInitToSurfaceState = (name, initOptions) => ({
  name,
  error: undefined,
  fallback: initOptions === null || initOptions === void 0 ? void 0 : initOptions.fallback
});
const transformSurfaceInitializationToClientSurfaceStates = surfaces => new Map(Object.entries(surfaces).map(([name, initOptions]) => [name, transformSurfaceInitToSurfaceState(name, initOptions)]));
const transformSurfaceInitializationToSurfaceByCombinedKey = surfaces => new Map(Object.entries(Object.entries(surfaces).reduce((a, [, initOptions]) => {
  var _initOptions$surfaceK, _initOptions$surface, _initOptions$surfaceT, _initOptions$surface2, _a$combinedKey, _a$combinedKey2;
  const surfaceKey = (_initOptions$surfaceK = initOptions.surfaceKey) !== null && _initOptions$surfaceK !== void 0 ? _initOptions$surfaceK : (_initOptions$surface = initOptions.surface) === null || _initOptions$surface === void 0 ? void 0 : _initOptions$surface.key;
  const surfaceType = (_initOptions$surfaceT = initOptions.surfaceType) !== null && _initOptions$surfaceT !== void 0 ? _initOptions$surfaceT : (_initOptions$surface2 = initOptions.surface) === null || _initOptions$surface2 === void 0 ? void 0 : _initOptions$surface2.type;
  const combinedKey = buildCombinedKey(surfaceType, surfaceKey);
  const updateId = createUpdateId();
  return {
    ...a,
    [combinedKey]: {
      updateId,
      surface: (a === null || a === void 0 ? void 0 : (_a$combinedKey = a[combinedKey]) === null || _a$combinedKey === void 0 ? void 0 : _a$combinedKey.surface) || (initOptions === null || initOptions === void 0 ? void 0 : initOptions.surface),
      isLoading: !(a !== null && a !== void 0 && (_a$combinedKey2 = a[combinedKey]) !== null && _a$combinedKey2 !== void 0 && _a$combinedKey2.surface || initOptions.load === 'PRE')
    }
  };
}, {})));
export const transformSurfaceInitializationToCombinedKeyByName = surfaces => new Map(Object.entries(surfaces).map(([name, initOptions]) => {
  var _initOptions$surfaceT2, _initOptions$surface3, _initOptions$surfaceK2, _initOptions$surface4;
  return [name, buildCombinedKey((_initOptions$surfaceT2 = initOptions.surfaceType) !== null && _initOptions$surfaceT2 !== void 0 ? _initOptions$surfaceT2 : (_initOptions$surface3 = initOptions.surface) === null || _initOptions$surface3 === void 0 ? void 0 : _initOptions$surface3.type, (_initOptions$surfaceK2 = initOptions.surfaceKey) !== null && _initOptions$surfaceK2 !== void 0 ? _initOptions$surfaceK2 : (_initOptions$surface4 = initOptions.surface) === null || _initOptions$surface4 === void 0 ? void 0 : _initOptions$surface4.key)];
}));
const createSurfaceStates = surfaces => {
  const clientSurfaceStates = transformSurfaceInitializationToClientSurfaceStates(surfaces);
  const surfaceCombinedKeyByName = transformSurfaceInitializationToCombinedKeyByName(surfaces);
  const surfacesByCombinedKey = transformSurfaceInitializationToSurfaceByCombinedKey(surfaces);
  return {
    set: (name, state) => {
      const surfaceCombinedKey = surfaceCombinedKeyByName.get(name);
      const updateId = createUpdateId();
      if (!surfaceCombinedKey || !clientSurfaceStates.has(name) || !surfacesByCombinedKey.has(surfaceCombinedKey)) {
        throw Error(`Tried to update a surface with name ${name}, but no surface of that name exists. Check your initialization settings`);
      }
      clientSurfaceStates.set(name, {
        name,
        fallback: state.fallback,
        error: state.error
      });
      surfacesByCombinedKey.set(surfaceCombinedKey, {
        updateId,
        surface: state.surface,
        isLoading: state.isLoading
      });
    },
    get: name => {
      try {
        const surfaceCombinedKey = surfaceCombinedKeyByName.get(name);
        const surfaceState = clientSurfaceStates.get(name);
        const surfaceByCombinedKey = surfacesByCombinedKey.get(surfaceCombinedKey);
        if (!surfaceState || !surfaceByCombinedKey) {
          throw Error;
        }
        return {
          ...surfaceState,
          ...surfaceByCombinedKey
        };
      } catch {
        throw Error(`Tried to get surface with name "${name}" but no surface of that name was initialized`);
      }
    },
    isSelected: (name, selectors) => !!selectors.find(selector => surfaceCombinedKeyByName.get(selector) === surfaceCombinedKeyByName.get(name))
  };
};
export const createSurfaceStore = surfaceInitializationOptions => {
  let surfaceStates = createSurfaceStates(surfaceInitializationOptions);
  const subscribers = new Map();
  const updateSubscribers = ({
    name
  }) => {
    const surfaceName = name;
    subscribers.forEach(subscriber => updateSubscriber(subscriber, surfaceName));
  };
  const updateSubscriber = (subscriber, surfaceName) => {
    const [subscription, selectors] = subscriber;
    if (!selectors || surfaceStates.isSelected(surfaceName, selectors)) {
      const state = surfaceStates.get(surfaceName);
      if (state) {
        subscription(state);
      }
    }
  };
  const subscribe = (...subscriber) => {
    const [subscription, selectors, options] = subscriber;
    subscribers.set(subscriber, subscriber);
    if (options !== null && options !== void 0 && options.updateOnSubscribe) {
      selectors === null || selectors === void 0 ? void 0 : selectors.forEach(selector => {
        updateSubscriber([subscription, [selector]], selector);
      });
    }
    return () => subscribers.delete(subscriber);
  };
  const setState = (newSurfaceState, callback) => {
    const surfaceName = newSurfaceState.name;
    surfaceStates.set(surfaceName, newSurfaceState);
    callback();
  };
  const update = (surfaceName, surfaceState) => {
    const previousSurfaceState = surfaceStates.get(surfaceName);
    const partialSurfaceState = typeof surfaceState === 'function' ? surfaceState(surfaceStates.get(surfaceName)) : surfaceState;
    const newSurfaceState = {
      ...previousSurfaceState,
      ...partialSurfaceState
    };
    setState(newSurfaceState, () => {
      updateSubscribers(newSurfaceState);
    });
  };
  const getCurrentSurfaceState = surfaceName => surfaceStates.get(surfaceName);
  const getCurrentSurfaceStateFromKeys = (surfaceType, surfaceKey) => surfaceStates.get(buildCombinedKey(surfaceType, surfaceKey));
  const getSurfaceInitializationOptions = surfaceName => {
    const surfaceOptions = surfaceInitializationOptions === null || surfaceInitializationOptions === void 0 ? void 0 : surfaceInitializationOptions[surfaceName];
    if (!surfaceOptions) {
      throw Error(`Tried to get surface initializations options for "${surfaceName}" but no surface of that name was initialized`);
    }
    return surfaceOptions;
  };
  const getSurfaces = () => {
    return surfaceInitializationOptions;
  };
  return {
    getSurfaceInitializationOptions,
    getCurrentSurfaceState,
    getCurrentSurfaceStateFromKeys,
    getSurfaces,
    update,
    subscribe
  };
};
function buildCombinedKey(SurfaceType, surfaceKey) {
  return `${SurfaceType},${surfaceKey}`;
}